import React, { useMemo } from "react";
//#region types
import { WintSuperkollAPIModelQualifiedShareDividend, WintUtilsWebApi } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { CurrencyEnums, TableEnums } from "models/enums";
import { QualifiedShareDividendListProps } from "models/propTypes/qualifiedShareDividendsPropTypes";
import { Amount } from "models/utilTypes/currencyType";
//#endregion
//#region components
import { TableAsync } from "components/common";
import QualifiedShareDividendStatusTags from "./statusTags";
import { Grid } from "@mui/material";
import { useQualifiedShareDividendsService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";

const QualifiedShareDividendList: React.FC<QualifiedShareDividendListProps> = (props) => {
  const qualifiedShareDividendsService = useQualifiedShareDividendsService();

  const getItemUrl = (id: number | string) => UrlHelper.formatPath(`/QualifiedShareDividend/${id}`);

  const filterFunc = useMemo(
    () => (baseFilter: WintUtilsWebApi.Filter) => qualifiedShareDividendsService.reportFilter(baseFilter),
    [qualifiedShareDividendsService]
  );

  const plannedStates = [
    WintSuperkollAPIModelQualifiedShareDividend.QualifiedShareDividendState.Pending,
    WintSuperkollAPIModelQualifiedShareDividend.QualifiedShareDividendState.Ongoing,
    WintSuperkollAPIModelQualifiedShareDividend.QualifiedShareDividendState.Rejected,
    WintSuperkollAPIModelQualifiedShareDividend.QualifiedShareDividendState.Manual,
    WintSuperkollAPIModelQualifiedShareDividend.QualifiedShareDividendState.ReadyToBeSentForApproval,
  ];

  const dividendFormatter = (row: WintSuperkollAPIModelQualifiedShareDividend.QualifiedShareDividendListItem) => {
    if (plannedStates.includes(row.state)) return "";
    return new Amount(CurrencyEnums.CurrencyCode.SEK, row.dividend ?? 0);
  };

  const ruleFormatter = (row: WintSuperkollAPIModelQualifiedShareDividend.QualifiedShareDividendListItem) => {
    if (plannedStates.includes(row.state)) return "";
    return qualifiedShareDividendsService.getRuleText(row.rule);
  };

  const groupFormatter = (row: WintSuperkollAPIModelQualifiedShareDividend.QualifiedShareDividendListItem) => {
    const { state } = row;
    if (plannedStates.includes(state)) {
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.QualifiedShareDividends.Planned);
    }
    if (state === WintSuperkollAPIModelQualifiedShareDividend.QualifiedShareDividendState.AwaitingApproval) {
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.QualifiedShareDividends.ToApprove);
    }
    return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.QualifiedShareDividends.Completed);
  };

  return (
    <Grid container>
      <TableAsync<WintSuperkollAPIModelQualifiedShareDividend.QualifiedShareDividendListItem>
        tableId={TableEnums.TableType.QualifiedShareDividendList}
        properties={{
          incomeYear: {
            description: TranslationKeys.QualifiedShareDividends.IncomeYear2,
            formatter: (row) => row.incomeYear.toString(),
          },
          dividend: {
            description: TranslationKeys.QualifiedShareDividends.Dividend,
            formatter: dividendFormatter,
            classification: "amount",
          },
          rule: {
            description: TranslationKeys.QualifiedShareDividends.Rule,
            formatter: ruleFormatter,
          },
          state: {
            description: TranslationKeys.Common.State,
            formatter: (row) => <QualifiedShareDividendStatusTags status={row.state} />,
          },
          group: {
            formatter: groupFormatter,
          },
        }}
        filter={filterFunc}
        itemUrl={getItemUrl}
        reloadKey={props.reloadKey}
        orderByProperty="incomeYear"
        orderByDescending
        includeSummary
      />
    </Grid>
  );
};

export default QualifiedShareDividendList;
